// Libraries
import dayjs, { Dayjs } from 'dayjs';
import localeData from 'dayjs/plugin/localeData';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import relativeFormat from 'dayjs/plugin/relativeTime';
import weekday from 'dayjs/plugin/weekday';
import _ from 'lodash';

// Locale
import i18n from '../locale/i18n';

// Media
import PatriotImage from '../media/images/equipment/p400.avif';
import VSIImage from '../media/images/equipment/VSI.avif';

// Types
import {
  LengthType,
  PressureType,
  SortOrder,
  TemperatureType,
} from '../types/global';
import { CrusherModel } from '../types/crusher';

dayjs.extend(localeData);
dayjs.extend(localizedFormat);
dayjs.extend(relativeFormat);
dayjs.extend(weekday);

export const DASHBOARD_NULLISH_TEXT: string = '--';

/** Convert a localized string to Number(float) format */
export const parseLocaleNumber = (stringNumber: string, locale: string) => {
  const numberFormat: Intl.NumberFormat = new Intl.NumberFormat(locale);

  const parts: Intl.NumberFormatPart[] = numberFormat.formatToParts(12345.6);
  const thousandSeparator: string | undefined = parts.find((part) => part.type === 'group')?.value;
  const decimalSeparator: string | undefined = parts.find((part) => part.type === 'decimal')?.value;

  const normalizedNumber = stringNumber
    .replace(new RegExp(`\\${thousandSeparator}`, 'g'), '')
    .replace(new RegExp(`\\${decimalSeparator}`), '.');

  return parseFloat(normalizedNumber);
};

/** Convert a hexadecimal color to a rgba color with an alpha value */
export const hexToRgba = (hex: string, alpha: number) => {
  const parsedHex: string = hex.replace('#', '');

  // Parse the hex values for red, green, and blue
  const r = parseInt(parsedHex.substring(0, 2), 16);
  const g = parseInt(parsedHex.substring(2, 4), 16);
  const b = parseInt(parsedHex.substring(4, 6), 16);

  // Ensure the alpha value is between 0 and 1
  const clampedAlpha: number = Math.min(1, Math.max(0, alpha));

  return `rgba(${r}, ${g}, ${b}, ${clampedAlpha})`;
};

/** Rounds the value to a specified number of decimal places */
export const roundNumber = (
  value: number | null | undefined,
  decimalPlaces: number = 0,
): number | null => {
  let result: number | null = null;

  if (!_.isNil(value)) {
    result = parseFloat(value.toFixed(decimalPlaces));
    if (Object.is(result, -0)) {
      result = 0;
    }
  }

  return result;
};

/** Format a Number to a default locale format - string */
export const formatNumber = (
  num: number | null | undefined,
  decimals: number | null = null,
): string => (
  !_.isNil(num)
    ? num.toLocaleString(
      i18n.language.replace('_', '-'),
      {
        style: 'decimal',
        minimumFractionDigits: decimals !== null ? decimals : undefined,
        maximumFractionDigits: decimals !== null ? decimals : undefined,
      },
    )
    : DASHBOARD_NULLISH_TEXT
);

/** Converts a timestamp to a localized date string in MMM DD, YYYY format */
export const timestampToDateStrText = (timestamp: number): string => {
  const dayObj: Dayjs = dayjs(timestamp);
  return dayObj.format('ll');
};

/** Converts a timestamp to a localized time string in HH:mm(am/pm) format */
export const timestampToTimeStr = (timestamp: number): string => {
  const dayObj: Dayjs = dayjs(timestamp);
  return dayObj.format('LT');
};

/** Converts a timestamp to a localized "[x] [unit] ago" format */
export const timestampToTimeAgo = (
  timestamp: number | null | undefined,
  nullishText: string = DASHBOARD_NULLISH_TEXT,
): string => {
  let result: string;

  if (!_.isNil(timestamp)) {
    const currentLocale: string = i18n.language.replace('_', '-').toLowerCase();
    dayjs.locale(currentLocale);

    const now: Dayjs = dayjs();
    let date: Dayjs = dayjs(timestamp);

    if (date.isAfter(now)) {
      date = now;
    }

    result = date.fromNow();
  } else {
    result = nullishText;
  }

  return result;
};

/** Converts file to base64 string */
export const convertFileToBase64 = (file: File | Blob): Promise<string> => (
  new Promise((resolve: (result: string) => void, reject: (error: Event) => void) => {
    const reader: FileReader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error: Event) => reject(error);
  })
);

/** Get the percentage of a value between a minimum and maximum value */
export const currentValuePercentage = ({
  value,
  minValue,
  maxValue,
}: {
  value: number | null | undefined;
  minValue: number | null | undefined;
  maxValue: number | null | undefined;
}): number => {
  if (_.isNil(value) || _.isNil(minValue) || _.isNil(maxValue)) {
    throw new Error('Error: [value], [minValue], and [maxValue] must be valid numbers');
  }

  if (minValue === maxValue) {
    throw new Error('Error: Invalid params, [maxValue] and [minValue] cannot be equal');
  }

  return ((value - minValue) / (maxValue - minValue)) * 100;
};

/** Get the browser window dimensions */
export const getWindowDimensions = (): { width: number, height: number } => {
  const { innerWidth: width, innerHeight: height } = window;
  return {
    width,
    height,
  };
};

/** Check the required environment variables */
export const validateEnvironmentVariables = (): void => {
  const requiredEnvVars: string[] = [
    'REACT_APP_ASYNC_TASK_API',
    'REACT_APP_CUSTOMER_API',
    'REACT_APP_USER_API',
    'REACT_APP_PLANT_API',
    'REACT_APP_EQUIPMENT_UNIT_API',
    'REACT_APP_EQUIPMENT_MODEL_API',
    'REACT_APP_EQUIPMENT_UNIT_DATA_MONITORING_API',
    'REACT_APP_GOOGLE_MAPS_API_KEY',
    'REACT_APP_GOOGLE_MAPS_MAP_ID',
  ];

  const missingVars: string[] = requiredEnvVars.filter((envVar: string) => !process.env[envVar]);

  if (missingVars.length > 0) {
    throw new Error(`Missing environment variables: ${missingVars.join(', ')}`);
  }
};

export function appLocaleToDayjs(locale: string | null | undefined): string {
  let result: string = '';

  // Validate the locale parameter and process it if valid
  if (typeof locale === 'string' && locale.trim() !== '') {
    result = locale.replace('_', '-').toLowerCase();
  }

  return result;
}

export const capitalizeFirstLetter = (str?: string | null): string | null => (!_.isNil(str)
  ? str.charAt(0).toUpperCase() + str.slice(1)
  : null);

/**
 * Validates if the email is in the correct format.
 * @param {string | null | undefined} email - The email to validate
 * @returns {boolean} True if the email is valid, false otherwise
 */
export const isValidEmail = (email: string | null | undefined): boolean => {
  if (_.isNil(email) || email.trim() === '') {
    return false;
  }

  // RFC 5322 compliant email regex
  const emailRegex: RegExp = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  return emailRegex.test(email);
};

/* **************** MEASUREMENT CONVERSION **************** */

interface ConversionResult {
  value: number | null;
  unit: string;
  decimalPlaces: number;
  formattedValue?: string;
}

export function convertTemperature(
  value: number | null | undefined,
  unit: TemperatureType,
  decimalPlaces: number = 0,
  showUnit?: boolean,
  tempDiff?: boolean,
): ConversionResult {
  const unitSymbol: string = unit === TemperatureType.f ? TemperatureType.f : TemperatureType.c;

  let convertedValue: number | null;
  let formattedValue: string | undefined;

  if (_.isNil(value)) {
    convertedValue = null;
    formattedValue = showUnit ? DASHBOARD_NULLISH_TEXT : undefined;
  } else {
    let processedValue: number = value;

    if (unit === TemperatureType.f) {
      processedValue = tempDiff ? (value * 9) / 5 : (value * 9) / 5 + 32;
    }

    convertedValue = roundNumber(processedValue, decimalPlaces);
    const formattedNumber: string = formatNumber(convertedValue, decimalPlaces);
    formattedValue = showUnit ? `${formattedNumber} ${unitSymbol}` : undefined;
  }

  return {
    value: convertedValue,
    unit: unitSymbol,
    decimalPlaces,
    formattedValue,
  };
}

export function convertPressure(
  value: number | null | undefined,
  unit: PressureType,
  decimalPlaces: number = 1,
  showUnit?: boolean,
): ConversionResult {
  let unitSymbol: string = PressureType.MPa;
  if (unit === PressureType.bar) {
    unitSymbol = PressureType.bar;
  } else if (unit === PressureType.PSI) {
    unitSymbol = PressureType.PSI;
  }

  let convertedValue: number | null;
  let formattedValue: string | undefined;

  if (_.isNil(value)) {
    convertedValue = null;
    formattedValue = showUnit ? DASHBOARD_NULLISH_TEXT : undefined;
  } else {
    let processedValue: number = value;
    if (unit === PressureType.bar) {
      processedValue = value * 10;
    } else if (unit === PressureType.PSI) {
      processedValue = value * 145.038;
    }

    convertedValue = roundNumber(processedValue, decimalPlaces);
    const formattedNumber: string = formatNumber(convertedValue, decimalPlaces);
    formattedValue = showUnit ? `${formattedNumber} ${unitSymbol}` : undefined;
  }

  return {
    value: convertedValue,
    unit: unitSymbol,
    decimalPlaces,
    formattedValue,
  };
}

export function convertLength(
  value: number | null | undefined,
  unit: LengthType,
  decimalPlaces: number = 1,
  showUnit?: boolean,
): ConversionResult {
  const unitSymbol: string = unit === LengthType.inch ? LengthType.inch : LengthType.mm;

  let convertedValue: number | null;
  let formattedValue: string | undefined;

  if (_.isNil(value)) {
    convertedValue = null;
    formattedValue = showUnit ? DASHBOARD_NULLISH_TEXT : undefined;
  } else {
    const processedValue: number = unit === LengthType.inch ? value * 0.0393701 : value;
    convertedValue = roundNumber(processedValue, decimalPlaces);
    const formattedNumber: string = formatNumber(convertedValue, decimalPlaces);
    formattedValue = showUnit ? `${formattedNumber} ${unitSymbol}` : undefined;
  }

  return {
    value: convertedValue,
    unit: unitSymbol,
    decimalPlaces,
    formattedValue,
  };
}

/** Formats values with fixed units */
export const formatValueWithUnit = (
  value: number | null | undefined,
  unit: string,
  decimalPlaces: number = 0,
): string => {
  let formattedResult: string = DASHBOARD_NULLISH_TEXT;

  if (!_.isNil(value)) {
    const roundedValue: number | null = roundNumber(value, decimalPlaces);
    const formattedValue: string = roundedValue !== null
      ? roundedValue.toFixed(decimalPlaces)
      : DASHBOARD_NULLISH_TEXT;

    if (!unit) {
      formattedResult = formattedValue;
    } else {
      // Units that should not have space (stick to the number)
      const noSpaceUnits: string[] = ['%'];
      const shouldAddSpace: boolean = !noSpaceUnits.includes(unit);

      formattedResult = shouldAddSpace ? `${formattedValue} ${unit}` : `${formattedValue}${unit}`;
    }
  }

  return formattedResult;
};

/**
 * Returns a random element from an array
 * @param options - Array of options to choose from
 * @returns Random element from the array
 * @throws Error if options array is empty
 */
export const randomChoice = <T>(options: T[]): T => {
  if (!options || options.length === 0) {
    throw new Error('The options list must not be empty');
  }

  return options[Math.floor(Math.random() * options.length)];
};

/**
 * Sorts an array of items by a specified key
 * @param items - Array of items to sort
 * @param sortKey - Key to sort by
 * @param order - Sort order (SortOrder.ascend or SortOrder.descend)
 * @returns Sorted array of items
 */
export const sortItems = <T extends Record<string, any>>(
  items: T[],
  sortKey: string,
  order: SortOrder = SortOrder.ascend,
): T[] => {
  const sortedItems: T[] = [...items].sort((a: T, b: T) => {
    const valA: string | number = a[sortKey];
    const valB: string | number = b[sortKey];

    let result: number = 0;
    if (valA < valB) result = -1;
    if (valA > valB) result = 1;

    return order === SortOrder.ascend ? result : -result;
  });

  return sortedItems;
};

/** Map of crusher models to their corresponding images */
export const CRUSHER_MODEL_IMAGE_MAP: Record<CrusherModel, string> = {
  [CrusherModel.p200]: PatriotImage,
  [CrusherModel.p300]: PatriotImage,
  [CrusherModel.p400]: PatriotImage,
  [CrusherModel.p500]: PatriotImage,
  [CrusherModel.p600]: PatriotImage,
  [CrusherModel.d350]: PatriotImage,
  [CrusherModel.vsi]: VSIImage,
};
